import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userActivityService } from "../services/service";

const EMPTY_PARAMS = {};
const USER_ACTIVITIES_KEY = ["user-activities"];

export const useUserActivities = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [actionFilter, setActionFilter] = useState("all");
    const [subjectFilter, setSubjectFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        activity: actionFilter !== "all" ? actionFilter : undefined,
        subject_id: subjectFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [currentPage, pageSize, searchTerm, actionFilter, subjectFilter, startDate, endDate, initialParams]);

    const query = useQuery({
        queryKey: [...USER_ACTIVITIES_KEY, "list", params],
        queryFn: () => userActivityService.getAll(params),
        staleTime: Infinity,
    });

    const statsQuery = useQuery({
        queryKey: [...USER_ACTIVITIES_KEY, "stats"],
        queryFn: () => userActivityService.getStats(),
        staleTime: 60_000,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: USER_ACTIVITIES_KEY }),
        [queryClient]
    );

    return {
        userActivities: query.data?.data?.data || [],
        pagination: query.data?.data?.meta || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        stats: statsQuery.data?.data?.data || statsQuery.data?.data || null,
        statsLoading: statsQuery.isLoading,

        searchTerm,
        setSearchTerm,
        actionFilter,
        setActionFilter,
        subjectFilter,
        setSubjectFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,

        refresh: query.refetch,
        refreshStats: statsQuery.refetch,
        invalidate,
    };
};

// Hook for a single activity
export const useUserActivity = (activityId) => {
    const query = useQuery({
        queryKey: [...USER_ACTIVITIES_KEY, "detail", activityId],
        queryFn: () => userActivityService.getById(activityId),
        enabled: Boolean(activityId),
        staleTime: Infinity,
    });

    return {
        userActivity: query.data?.data?.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        refresh: query.refetch,
    };
};

// Hook for activities by user log id (session NanoID)
export const useUserActivitiesByUser = (userlogId, initialParams = EMPTY_PARAMS) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        ...initialParams,
    }), [currentPage, pageSize, initialParams]);

    const query = useQuery({
        queryKey: [...USER_ACTIVITIES_KEY, "by-log", userlogId, params],
        queryFn: () => userActivityService.getByUser(userlogId, params),
        enabled: Boolean(userlogId),
        staleTime: Infinity,
    });

    return {
        userActivities: query.data?.data || [],
        pagination: query.data?.meta || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        refresh: query.refetch,
    };
};

// Hook for activities by subject
export const useUserActivitiesBySubject = (subjectId, initialParams = EMPTY_PARAMS) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        ...initialParams,
    }), [currentPage, pageSize, initialParams]);

    const query = useQuery({
        queryKey: [...USER_ACTIVITIES_KEY, "subject", subjectId, params],
        queryFn: () => userActivityService.getBySubject(subjectId, params),
        enabled: Boolean(subjectId),
        staleTime: Infinity,
    });

    return {
        userActivities: query.data?.data?.data || [],
        pagination: query.data?.data?.meta || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        refresh: query.refetch,
    };
};